import { useCallback, useMemo, useEffect, useRef } from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/app/providers/useAuth";
import { useAllowedPageSizes } from "@/app/providers/useAllowedPageSizes";
import { FileExplorerTabs } from "./FileExplorerTabs";
import { UploadModal } from "../upload-modal/UploadModal";
import { FileExplorerContent } from "./FileExplorerContent";
import { FileExplorerLoadingState } from "./FileExplorerLoadingState";
import { FileExplorerErrorState } from "./FileExplorerErrorState";
import { FileExplorerImageDialog } from "./FileExplorerImageDialog";
import { useFileExplorerUi } from "../../store/fileExplorerUi.store";
import { useFileExplorerData } from "../../hooks/file-explorer/useFileExplorerData";
import { useFileSelection } from "../../hooks/file-explorer/useFileSelection";
import { useFileExplorerFilters } from "../../hooks/file-explorer/useFileExplorerFilters";
import { useFileExplorerModals } from "../../hooks/file-explorer/useFileExplorerModals";
import { useFileExplorerPagination } from "../../hooks/file-explorer/useFileExplorerPagination";
import { useFileExplorerSorting } from "../../hooks/file-explorer/useFileExplorerSorting";
import { useImageDialogNavigation } from "../../hooks/image-dialog/useImageDialogNavigation";
import { useCategoryAttributesDisplay } from "@/features/admin/panels/categories/hooks/useCategoryAttributesDisplay";
import { useCategoryTabsFromApi } from "../../hooks/useCategoryTabsFromApi";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { getCategoryLabel } from "../../utils/categoryUtils";
import type { FileExplorerSearchParams } from "../../types";

export function FileExplorer() {
  const { roles } = useAuth();
  const canDeleteFiles = useMemo(
    () => roles.some((role) => role === "Admin" || role === "User"),
    [roles]
  );

  const { viewMode, setViewMode, currentPage, itemsPerPage, setCurrentPage } =
    useFileExplorerUi();

  const search = useSearch({
    from: "/file-explorer/$categoryName",
  }) as FileExplorerSearchParams;
  const navigate = useNavigate({ from: "/file-explorer/$categoryName" });

  const activeTab = search.tab;

  const modals = useFileExplorerModals();
  const { data: allowedPageSizesData } = useAllowedPageSizes();
  const allowedPageSizes = allowedPageSizesData?.allowedPageSizes || [
    60, 30, 15,
  ];

  const {
    matchedCategory,
    category,
    categoryError,
    categoryAttributes,
    filters,
    allFiles,
    totalPages,
    totalFiles,
    isLoading,
    galleryError,
  } = useFileExplorerData({
    currentPage,
    itemsPerPage,
  });

  useDocumentTitle(getCategoryLabel(category));

  const { attributes: displayAttributes } = useCategoryAttributesDisplay({
    category: category ?? null,
    enabled: !!category?.id,
  });

  const {
    validSelectedFile,
    handleFileSelect,
    handleCloseDetails,
    handleFileNavigate,
  } = useFileSelection({ allFiles });

  const { handleFiltersChange } = useFileExplorerFilters({
    categoryAttributes,
  });

  const { handlePageChange, handleItemsPerPageChange } =
    useFileExplorerPagination();

  const { sortBy, sortDirection, handleSort } = useFileExplorerSorting();

  const {
    imageDialog,
    handleImageClick,
    handleImageDialogOpenChange,
    handleImageDialogNavigate,
  } = useImageDialogNavigation({ allFiles });

  const { tabs: categoryTabs, defaultTab } = useCategoryTabsFromApi(
    category?.id
  );

  const handleTabChange = useCallback(
    (tab: string) => {
      navigate({
        search: { tab } as any,
        replace: true,
      });
      setCurrentPage(1);
    },
    [navigate, setCurrentPage]
  );

  const availableTabValues = useMemo(
    () => categoryTabs.map((tab) => tab.value),
    [categoryTabs]
  );

  const lastCategoryIdRef = useRef<string | number | undefined>(undefined);
  const hasSetDefaultTabRef = useRef(false);

  useEffect(() => {
    if (lastCategoryIdRef.current !== category?.id) {
      lastCategoryIdRef.current = category?.id;
      hasSetDefaultTabRef.current = false;
      setCurrentPage(1);
    }

    if (
      category?.id &&
      defaultTab !== null &&
      categoryTabs.length > 0 &&
      !hasSetDefaultTabRef.current &&
      (activeTab === undefined || !availableTabValues.includes(activeTab))
    ) {
      hasSetDefaultTabRef.current = true;
      navigate({
        search: {
          ...search,
          tab: defaultTab,
        },
        replace: true,
      });
    }
  }, [
    category?.id,
    activeTab,
    availableTabValues,
    defaultTab,
    categoryTabs.length,
    navigate,
    search,
    setCurrentPage,
  ]);

  if (isLoading) {
    return <FileExplorerLoadingState />;
  }

  if (!matchedCategory || categoryError || !category) {
    return <FileExplorerErrorState categoryError={categoryError} />;
  }

  return (
    <div className="flex flex-1 min-h-0 flex-col min-w-0 w-full max-w-full overflow-hidden">
      <FileExplorerTabs
        activeTab={activeTab ?? defaultTab ?? ""}
        onTabChange={handleTabChange}
        categoryId={category?.id}
        tabs={categoryTabs}
      />

      <FileExplorerContent
        activeTab={activeTab ?? defaultTab ?? ""}
        tabs={categoryTabs}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        isFiltersOpen={modals.isFiltersOpen}
        onFiltersToggle={modals.toggleFilters}
        onUploadOpen={modals.openUpload}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        allFiles={allFiles}
        validSelectedFile={validSelectedFile}
        matchedCategoryId={
          matchedCategory?.id ? String(matchedCategory.id) : undefined
        }
        categoryAttributes={displayAttributes}
        filterCategoryAttributes={categoryAttributes}
        galleryError={galleryError}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onFileSelect={handleFileSelect}
        onSort={handleSort}
        onImageClick={handleImageClick}
        canDeleteFiles={canDeleteFiles}
        onCloseDetails={handleCloseDetails}
        onFileNavigate={handleFileNavigate}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalFiles={totalFiles}
        allowedPageSizes={allowedPageSizes}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {category && (
        <UploadModal
          open={modals.isUploadModalOpen}
          onOpenChange={modals.setIsUploadModalOpen}
          category={category}
        />
      )}

      <FileExplorerImageDialog
        imageFile={imageDialog.imageFile}
        isOpen={imageDialog.isOpen}
        allFiles={allFiles}
        onOpenChange={handleImageDialogOpenChange}
        onNavigate={handleImageDialogNavigate}
      />
    </div>
  );
}
